import type { RecordingCapHitsBucket, RecordingCapHitsResponse } from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface RecordingCapHitsProps {
  data: RecordingCapHitsResponse;
}

function BucketRow({ title, bucket }: { title: string; bucket: RecordingCapHitsBucket }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MetricCard label="Różnych osób" value={bucket.distinctUsers} />
        <MetricCard label="Dobić łącznie" value={bucket.totalEvents} />
      </div>
    </div>
  );
}

/**
 * M-C panel (checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md,
 * V2M-DEV-012): licznik dobić do twardego limitu nagrania. Endpoint
 * `GET /admin/metrics/recording-cap-hits` istniał bez ani jednego
 * konsumenta we froncie (grep zero trafień) — ten komponent jest tym
 * konsumentem.
 *
 * Dwie liczby na kubełek, nigdy jedna — patrz backend
 * `get_recording_cap_hits` docstring: "dziesięć dobić jednego zapaleńca to
 * inny sygnał niż jedno dobicie u dziesięciu różnych osób." `distinctUsers`
 * i `totalEvents` renderowane obok siebie, nigdy zwinięte w jedną liczbę.
 *
 * `hoursSinceLastEventEver` (V2M-DEV-167): backend dołożył to pole 2026-08-04
 * dokładnie po to, żeby trzy wyzerowane kubełki dało się odróżnić od
 * kolekcji, do której nigdy nic nie trafiło — i przez dobę nie miało tu ani
 * jednego konsumenta. `null` = `recording_cap_hits` NIGDY nie dostała zapisu
 * (najgłośniejsze możliwe ustalenie, czerwone); liczba = pipeline żyje i
 * pisał tyle godzin temu, choćby okno było puste. Typ pochodzi z
 * WYGENEROWANEGO kontraktu, nie z ręcznej kopii w `src/types/api.ts` — to ta
 * kopia sprawiła, że dołożenie pola nie wywołało żadnego błędu kompilacji.
 */
export function RecordingCapHits({ data }: RecordingCapHitsProps) {
  if (data.hoursSinceLastEventEver === null) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">
          NIGDY nie zapisano do <code>recording_cap_hits</code> — ani razu, w całej historii, nie
          tylko w ostatnie {data.days} {data.days === 1 ? "dobę" : "dni"}. To nie jest zero dobić,
          to brak pomiaru.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Ostatnie {data.days} {data.days === 1 ? "doba" : "dni"} — ile razy nagranie zostało
        zatrzymane przez twardy limit (nie przez użytkownika), rozbite na poziom konta.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MetricCard
          label="Godzin od ostatniego dobicia (kiedykolwiek)"
          value={data.hoursSinceLastEventEver.toFixed(1)}
        />
      </div>
      <BucketRow title="Wszystkie konta — dobicia" bucket={data.overall} />
      <BucketRow title="Free — dobicia do 180 s" bucket={data.free} />
      <BucketRow title="Płatne — dobicia do 300 s" bucket={data.paid} />
    </div>
  );
}
